/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { tokens } from '@neo4j-ndl/base';
import {
  type MouseEvent,
  type TouchEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import type { Radius, Spacing } from './types';

export const removeNewlines = (input: string) =>
  input.replace(/(\r\n|\n|\r)/gm, '');

/** Remove extra spaces from sting */
export const removeSpaces = (input: string) =>
  input.replace(/\s+/g, ' ').trim();

export const needleWarningMessage = (message: string) =>
  console.warn(`[🪡 Needle]:  ${message}`);

const getScrollbarSize = (doc: Document): number => {
  // https://developer.mozilla.org/en-US/docs/Web/API/Window/innerWidth#usage_notes
  const documentWidth = doc.documentElement.clientWidth;
  const scrollBarSize = Math.abs(window.innerWidth - documentWidth);
  // Firefox with 110% and 120% zoom level return 1px even if there is no scrollbar
  return scrollBarSize > 1 ? scrollBarSize : 0;
};

const getPaddingRight = (element: Element): number =>
  parseInt(window.getComputedStyle(element).paddingRight, 10) || 0;

/**
 * Toggles scroll on the provided document.
 * Useful for disabling scroll when a popup is open (ie ContextMenu/Modal)
 */
export const useDocumentScrollToggle = () => {
  const bodyPadding = useRef<number>(0);

  return useCallback((disable: boolean, doc: Document = document) => {
    if (disable) {
      const existingPaddingRight = getPaddingRight(doc.body);
      bodyPadding.current = existingPaddingRight;
      const newPaddingRight = existingPaddingRight + getScrollbarSize(doc);
      doc.body.style.overflow = 'hidden';
      doc.body.style.paddingRight = `${newPaddingRight}px`;
    } else {
      doc.body.style.overflow = '';
      doc.body.style.paddingRight = `${bodyPadding.current}px`;
    }
  }, []);
};

/**
 * Detect if there is a click event outside
 * of the provided element
 * Source:
 * https://hashnode.com/post/useonclickoutside-custom-hook-to-detect-the-mouse-click-on-outside-typescript-ckrejmy3h0k5r91s18iu42t28
 */
export const useOnClickOutside = <T extends HTMLElement = HTMLElement>(
  ref: React.RefObject<T>,
  handler: (event: MouseEvent | TouchEvent) => void,
) => {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      const el = ref?.current;
      if (!el || el.contains((event?.target as Node) || null)) {
        return;
      }

      handler(event); // Call the handler only if the click is outside of the element passed.
    };

    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    document.addEventListener('mousedown', listener as any);
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    document.addEventListener('touchstart', listener as any);

    return () => {
      // oxlint-disable-next-line @typescript-eslint/no-explicit-any
      document.removeEventListener('mousedown', listener as any);
      // oxlint-disable-next-line @typescript-eslint/no-explicit-any
      document.removeEventListener('touchstart', listener as any);
    };
  }, [ref, handler]); // Reload only if ref or handler changes
};

/**
 *  X - char long pseudo-random string
 */
export const randomId = (length: number) =>
  Math.random()
    .toString(36)
    .slice(2, length + 2);

// Utility / Type-Guard if the object is a ref
export function isRefObject<T>(
  obj: React.RefObject<T> | T,
): obj is React.RefObject<T> {
  return obj && typeof obj === 'object' && 'current' in obj;
}

/**
 * Equivalent to prevUntil/nextUntil in jQuery
 * https://api.jquery.com/prevUntil/
 * https://api.jquery.com/nextUntil/
 *
 * Additional functionality is added to circle back
 * to the beginning of a list of siblings if it reaches the end
 * or vice versa.
 */
export function findUntil(
  direction: 'next' | 'prev',
  el: Element | ChildNode,
  matchSelector: string,
): Element | null {
  const element: ChildNode = el;

  if (!element.parentElement) {
    return null;
  }

  const allSiblings = [...element.parentElement.children].filter((sibling) =>
    sibling.matches(matchSelector),
  );

  const index = allSiblings.findIndex((sibling) =>
    sibling.isEqualNode(element),
  );

  if (index === -1) {
    return null;
  }

  const newIndex =
    direction === 'next'
      ? (index + 1) % allSiblings.length
      : (index - 1 + allSiblings.length) % allSiblings.length;

  return allSiblings[newIndex];
}

/**
 * Find all html elements that are focusable given a parent element
 * @param parentElement - The parent element
 * @returns an array of HTML elements
 */
export const findFocusableChildren = (parentElement: HTMLElement) => {
  const focusableElements =
    'a:not([disabled]), button:not([disabled]), input[type=text]:not([disabled]), input[type=checkbox]:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])';

  const focusable = Array.from(
    parentElement.querySelectorAll(focusableElements),
  ).filter((element) => {
    if (element instanceof HTMLElement) {
      return true;
    }

    return false;
  });
  return focusable;
};

/**
 * Using the element that currently has focus, finds the previous sibling of the currently focused element that is focusable
 * @param parentRef - The parent element
 * @returns the previous focusable element
 */
export const findFocusableSibling = (
  parentRef: React.RefObject<HTMLElement | null>,
  direction: 'prev' | 'next',
): HTMLElement | undefined => {
  const { current } = parentRef;
  const { activeElement } = document;

  if (current === null || activeElement === null) {
    return undefined;
  }

  const focusable = findFocusableChildren(current);
  const index = focusable.indexOf(activeElement);
  if (index > -1) {
    const siblingElement = focusable[index + (direction === 'next' ? 1 : -1)];
    if (
      siblingElement === undefined ||
      !(siblingElement instanceof HTMLElement)
    ) {
      return undefined;
    }
    return siblingElement;
  }
  return undefined;
};

/**
 * Convert hex color to rgb format
 *
 * @param hex - Color in hex code format
 * @returns color in rgb format rgb(_, _, _)
 */
export const convertHexToRGB = (hex: string) => {
  hex = hex.replace(/^#/, '');

  const red = parseInt(hex.substring(0, 2), 16);
  const green = parseInt(hex.substring(2, 4), 16);
  const blue = parseInt(hex.substring(4, 6), 16);

  return `rgb(${red}, ${green}, ${blue})`;
};

export const SpacingDefaults = {
  gap: '4',
  padding: '4',
  paddingBlockEnd: undefined,
  paddingBlockStart: undefined,
  paddingInline: undefined,
  paddingInlineEnd: undefined,
  paddingInlineStart: undefined,
} as const;

export const getSpacing = (
  spacing: Spacing | undefined,
  property: keyof typeof SpacingDefaults,
) => {
  return spacing
    ? tokens.space[spacing || SpacingDefaults[property]]
    : undefined;
};

export const RadiusDefaults = {
  borderRadius: undefined,
};

export const getRadius = (
  spacing: Radius | undefined,
  property: keyof typeof RadiusDefaults,
) => {
  return spacing
    ? tokens.borderRadius[spacing || RadiusDefaults[property]]
    : undefined;
};

export const parseRgbString = (rgbString: string): [number, number, number] => {
  const match = rgbString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) {
    throw new Error(`Unable to parse RGB color: ${rgbString}`);
  }
  return [Number(match[1]), Number(match[2]), Number(match[3])];
};
